//@ts-strict-ignore
import React, { Component, useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { BlurTextField, DateField, Dropdown } from '../connectedForm/inputComponents';
import getValue from './utils/getValue';
import { default as Fieldset } from './Fieldset';
import { default as Row } from './Row';
import Checkbox from './Checkbox/Checkbox';
import {
  AssemblyAttributeConfig,
  CustomerAttributeConfig,
  CustomerContactAttributeConfig,
  DiaryAttributeConfig,
  ProductAttributeConfig,
  ProductCategoryAttributeConfig,
  PurchaseAttributeConfig,
  SalesAttributeConfig,
  SupplierAttributeConfig,
  SupplierContactAttributeConfig,
} from '../../graphql/entities';
import { uuIdGenerator } from '../../utils/UuIdGenerator';
import generateClientMutationId from '../../graphql/schema/generateClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import UserContext from '../Context/UserContext';
import { EntityType } from '../../@types/entityTypes';
import {
  ICustomerAttributeConfig,
  ICustomerAttributeValue,
  ICustomerContactAttributeConfig,
  ICustomerContactAttributeValue,
  IDiaryAttributeConfig,
  IDiaryAttributeValue,
  IProductAttributeConfig,
  IProductAttributeValue,
  ISupplierAttributeConfig,
  ISupplierAttributeValue,
  ISupplierContactAttributeConfig,
  ISupplierContactAttributeValue,
} from '../../graphql/schema/p4types';
import { Dispatch } from 'redux';
import ICommonFieldProps from '../../@types/ICommonFieldProps';
import { cloneDeep } from '@apollo/client/utilities';

const useAttributeConfig = (entity, entityType, id) => {
  const { data } = useQuery(entity.instance, { variables: { id }, fetchPolicy: 'network-only' });
  const [attributeConfig, setAttributeConfig] = useState(data);
  useEffect(() => {
    if (data?.data) {
      setAttributeConfig(data?.data);
    }
  }, [data?.data]);

  const [saveAttributeConfig] = useMutation(entity.update, {
    onCompleted: (data) => {
      setAttributeConfig(data[`${entityType}Update`].result);
    },
  });

  return {
    attributeConfig: attributeConfig,
    save: saveAttributeConfig,
  };
};

type IAttributeConfigEntity = {
  entity: EntityType;
  type: string;
  referenceType: string;
};

const useAttributeConfigEntityFromType: (type: string) => IAttributeConfigEntity | null = (type) => {
  switch (type) {
    case 'supplierAttributeConfig':
      return {
        entity: SupplierAttributeConfig,
        type: 'supplierAttributeConfig',
        referenceType: 'supplierAttributeReference',
      };
    case 'customerContactAttributeConfig':
      return {
        entity: CustomerContactAttributeConfig,
        type: 'customerContactAttributeConfig',
        referenceType: 'customerContactAttributeReference',
      };
    case 'supplierContactAttributeConfig':
      return {
        entity: SupplierContactAttributeConfig,
        type: 'supplierContactAttributeConfig',
        referenceType: 'supplierContactAttributeReference',
      };
    case 'productAttributeConfig':
      return {
        entity: ProductAttributeConfig,
        type: 'productAttributeConfig',
        referenceType: 'productAttributeReference',
      };
    case 'diaryAttributeConfig':
      return {
        entity: DiaryAttributeConfig,
        type: 'diaryAttributeConfig',
        referenceType: 'diaryAttributeReference',
      };
    case 'salesAttributeConfig':
      return {
        entity: SalesAttributeConfig,
        type: 'salesAttributeConfig',
        referenceType: 'salesAttributeReference',
      };
    case 'purchaseAttributeConfig':
      return {
        entity: PurchaseAttributeConfig,
        type: 'purchaseAttributeConfig',
        referenceType: 'purchaseAttributeReference',
      };
    case 'customerAttributeConfig':
      return {
        entity: CustomerAttributeConfig,
        type: 'customerAttributeConfig',
        referenceType: 'customerAttributeReference',
      };
    case 'assemblyAttributeConfig':
      return {
        entity: AssemblyAttributeConfig,
        type: 'assemblyAttributeConfig',
        referenceType: 'assemblyAttributeReference',
      };
    case 'productCategoryAttributeConfig':
      return {
        entity: ProductCategoryAttributeConfig,
        type: 'productCategoryAttributeConfig',
        referenceType: 'productCategoryAttributeReference',
      };
  }

  return null;
};

const AttributeDropdown = ({ childProps, attribute, label, onChange: onChangeFromProps, disabled }) => {
  const { entity, type, referenceType } = useAttributeConfigEntityFromType(
    attribute?.attributeConfig?.type
  ) as IAttributeConfigEntity;
  const { attributeConfig: rawAttributeConfig, save } = useAttributeConfig(entity, type, attribute.attributeConfig.id);
  const {
    currentUser: {
      companyUser: { allowDynamicAttributeEntry = true },
    },
  } = useContext(UserContext);

  //Note: This code may not be necessary once on SPQR 0.11.2 (~1.3.0 Targeted).
  const attributeConfig = normalizeVariables(rawAttributeConfig, ['suppressMetaActions']);

  const onChange = (value) => {
    //Save new attribute reference value
    if (allowDynamicAttributeEntry && value?.className === 'Select-create-option-placeholder') {
      const newAttributeReference = {
        description: value.description,
        uuid: uuIdGenerator(),
        type: referenceType,
      };

      save({
        variables: {
          input: {
            clientMutationId: generateClientMutationId(),
            [type]: normalizeVariables({
              ...attributeConfig,
              attributeReferenceList: [...attributeConfig.attributeReferenceList, newAttributeReference],
            }),
          },
        },
      }).then((result) => {
        const newAttributeReferenceList = result.data[`${type}Update`].result.attributeReferenceList;
        onChangeFromProps(
          newAttributeReferenceList.find((newAttribute) => newAttribute.uuid === newAttributeReference.uuid)
        );
      });
      return;
    }

    onChangeFromProps(value);
  };

  return (
    <Dropdown
      {...childProps}
      value={attribute.referenceValue || null}
      options={attributeConfig?.attributeReferenceList ? [...attributeConfig.attributeReferenceList] : []}
      valueKey={'id'}
      labelKey={'description'}
      label={label}
      onChange={onChange}
      rawLabel={true}
      readOnly={disabled || childProps.readOnly}
      creatable={allowDynamicAttributeEntry}
    />
  );
};

export type IAttributeConfig =
  | ICustomerAttributeConfig
  | ISupplierAttributeConfig
  | IProductAttributeConfig
  | IDiaryAttributeConfig
  | ICustomerContactAttributeConfig
  | ISupplierContactAttributeConfig;

export type IAttributeFieldProps = ICommonFieldProps & {
  value:
    | ICustomerAttributeValue
    | ISupplierAttributeValue
    | IProductAttributeValue
    | IDiaryAttributeValue
    | ICustomerContactAttributeValue
    | ISupplierContactAttributeValue;
  attributeConfig?: IAttributeConfig;
  type?: string;
  ignoreTypeForValue?: boolean;
  onBlur?: Function;
  onChange?: Function;
  overrideAttributeLabel?: boolean;
  label?: string;
  active?: boolean;
  clearButton?: any;
  connectedForm?: any;
  customValidationErrors?: any;
  dirty?: boolean;
  dispatch?: Dispatch;
  hint?: string;
  id?: string;
  includeLabel?: boolean;
  onFocus?: Function;
  pristine?: boolean;
  readOnly?: boolean;
  showWhenNotActive?: boolean;
  temporary?: boolean;
  testId?: string;
  touched?: boolean;
  validationErrors?: any;
  validationHint?: string;
  visited?: boolean;
  mandatory?: boolean;
  disabled?: boolean;
  includeCheckbox?: boolean;
  dataid?: string;
  data?: any;
};

export default class AttributeField extends Component<IAttributeFieldProps, { error: any; errorInfo: any }> {
  constructor(props) {
    super(props);

    this.renderComponent = this.renderComponent.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);

    this.processValue = this.processValue.bind(this);
  }

  getAttributeConfig() {
    const attribute = this.props.value;

    let { attributeConfig } = this.props;

    if (!attributeConfig) {
      if (!attribute) return null;
      attributeConfig = attribute.attributeConfig as IAttributeConfig;
    }

    const {
      id,
      concurrencyVersion,
      attributeType,
      createdAt,
      createdBy,
      mandatory,
      sortOrder,
      title,
      updatedAt,
      updatedBy,
      uuid,
      attributeReferenceList,
    } = attributeConfig;

    //Note - Need to exclude nested entities or we will cause a recursive object exception.
    return {
      id,
      concurrencyVersion,
      attributeType,
      createdAt,
      createdBy,
      mandatory,
      sortOrder,
      title,
      updatedAt,
      updatedBy,
      uuid,
      attributeReferenceList,
      type: `${this.props.type}AttributeConfig`,
    };
  }

  processValue(rawValue) {
    const newValue = getValue(rawValue);
    let { value: attributeReadOnly } = this.props;
    const attribute = cloneDeep(attributeReadOnly) ?? {};
    const { attributeReferenceList, ...attributeConfig } = this.getAttributeConfig() as IAttributeConfig;
    attribute.attributeConfig = attributeConfig;

    switch (attribute.attributeConfig.attributeType as string) {
      case 'REFERENCE':
        // If the field is for setting a default value and the rawValue is null then return null
        if (rawValue === null) {
          attribute.referenceValue = null;
          break;
        }
        attribute.referenceValue = {
          type: `${this.props.type}AttributeReference`,
          ...newValue,
        };
        break;
      case 'DATE':
        attribute.dateValue = newValue;
        break;
      case 'INTEGER':
        attribute.integerValue = parseInt(newValue, 10);
        break;
      case 'NUMERIC':
      case 'DOUBLE':
        attribute.doubleValue = newValue;
        break;
      default:
        attribute.textValue = newValue;
        break;
    }

    //ignoreTypeForValue prop is because 'DefaultSalesAttributeValue' does not have/require a type field like with the actual values.
    if (!this.props.ignoreTypeForValue) {
      attribute.type = `${this.props.type}AttributeValue`;
    }

    return attribute;
  }

  onBlur(rawValue) {
    const attribute = this.processValue(rawValue);

    this.props.onBlur?.(attribute);
  }

  onChange(rawValue) {
    const attribute = this.processValue(rawValue);
    this.props.onChange?.(attribute);
  }

  renderCheckbox = (attribute) => {
    return (
      <Checkbox
        value={attribute.amendValue}
        onChange={(rawValue) => {
          attribute.amendValue = getValue(rawValue);
          this.props.onChange?.(attribute);
        }}
      />
    );
  };

  renderComponent() {
    const { value: attributeReadOnly } = this.props;
    const attribute = cloneDeep(attributeReadOnly) ?? {};

    const { attributeReferenceList, ...attributeConfig } = this.getAttributeConfig() as IAttributeConfig;
    attribute.attributeConfig = attributeConfig;

    if (!attribute.attributeConfig) return null;

    let valueKey;

    const label = this.props.overrideAttributeLabel ? this.props.label : attribute.attributeConfig.title;

    let { validationErrors } = this.props;

    const {
      active,
      clearButton,
      connectedForm,
      customValidationErrors,
      dirty,
      dispatch,
      hint,
      id,
      includeLabel,
      onFocus,
      pristine,
      readOnly,
      showWhenNotActive,
      temporary,
      testId,
      touched,
      validationHint,
      visited,
      inputCols,
      labelCols,
    } = this.props;

    if (validationErrors && validationErrors.submit) {
      const filteredErrors = validationErrors.submit.filter((error) =>
        error.includes(attribute.attributeConfig?.title)
      );
      validationErrors = {
        ...validationErrors,
      };

      if (filteredErrors.length > 0) {
        validationErrors.submit = filteredErrors;
      } else {
        validationErrors = null;
      }
    }

    const childProps = {
      active,
      clearButton,
      connectedForm,
      customValidationErrors,
      dirty,
      dispatch,
      hint,
      id,
      includeLabel,
      mandatory: attribute.attributeConfig.mandatory || this.props.mandatory || false,
      onBlur: this.onBlur,
      onFocus,
      pristine,
      readOnly,
      showWhenNotActive,
      temporary,
      testId,
      touched,
      validationErrors,
      validationHint,
      visited,
      inputCols,
      labelCols,
    };

    switch (attribute.attributeConfig.attributeType as string) {
      case 'REFERENCE':
        return (
          <AttributeDropdown
            childProps={childProps}
            attribute={attribute}
            label={label}
            onChange={this.onChange}
            disabled={this.props.disabled}
          />
        );
      case 'DATE':
        return (
          <DateField
            {...childProps}
            value={attribute.dateValue}
            label={label}
            onChange={this.onChange}
            disabled={this.props.disabled}
          />
        );
      case 'INTEGER':
        valueKey = 'integerValue';
        break;
      case 'NUMERIC':
      case 'DOUBLE':
        valueKey = 'doubleValue';
        break;
      default:
        valueKey = 'textValue';
        break;
    }

    return (
      <BlurTextField
        {...childProps}
        value={attribute[valueKey]}
        label={label}
        onChange={this.onChange}
        disabled={this.props.disabled}
        enableOnChange
      />
    );
  }

  render() {
    const attribute = this.props.value;

    if (this.props.includeCheckbox) {
      return (
        <Row key={`_${attribute.attributeConfig?.id}`}>
          <Fieldset cols={1 / 15} collapsed={true}>
            {this.renderCheckbox(attribute)}
          </Fieldset>
          <Fieldset cols={5 / 8} collapsed={true}>
            {this.renderComponent()}
          </Fieldset>
        </Row>
      );
    }
    return this.renderComponent();
  }
}
